import { LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline'
import { useLockContext } from 'context/LockContext'
import React, { useCallback } from 'react'
import { Tooltip } from 'stories/components'

import { isEffectivelyLocked, isExplicitlyUnlocked, LockScope, lockScopeToKey } from './types'

interface LockSectionControlProps {
  scope: LockScope
  label?: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
  childScopes?: LockScope[]
}

const sizeMap = {
  sm: 'w-3.5 h-3.5',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
}

export const LockSectionControl: React.FC<LockSectionControlProps> = ({
  scope,
  label,
  className = '',
  size = 'md',
  childScopes,
}) => {
  const { locks, canLock, setLock, setLockBatch, isBaseDisabled } = useLockContext()

  if (!canLock) return null

  const key = lockScopeToKey(scope)
  const directValue = locks[key] as 0 | 1 | undefined
  const directlyLocked = directValue === 1
  const isUnset = directValue === undefined
  const inheritedLock = isUnset && isEffectivelyLocked(locks, scope)
  const lockFeatureLocked = directlyLocked || inheritedLock
  const baseDisabledLock = !lockFeatureLocked && !isExplicitlyUnlocked(locks, scope) && isBaseDisabled(scope)
  const locked = lockFeatureLocked || baseDisabledLock

  const handleToggle = useCallback(async () => {
    const nextLocked = !locked
    if (childScopes && childScopes.length > 0) {
      const updates: Array<{ scope: LockScope; locked: boolean | null }> = [{ scope, locked: nextLocked }]
      for (const childScope of childScopes) {
        const childKey = lockScopeToKey(childScope)
        if (locks[childKey] !== undefined) {
          updates.push({ scope: childScope, locked: null })
        }
      }
      await setLockBatch(updates)
      return
    }
    await setLock(scope, nextLocked)
  }, [scope, childScopes, locked, locks, setLock, setLockBatch])

  const Icon = locked ? LockClosedIcon : LockOpenIcon
  const iconClass = sizeMap[size]

  const verb = locked ? 'Unlock' : 'Lock'
  const tooltipLabel =
    scope.type === 'borrower' ? `${verb} All fields for ${label ?? 'this borrower'}` : label ?? `${verb} ${scope.type}`

  const color = inheritedLock
    ? 'text-orange-400'
    : directlyLocked
    ? 'text-red-600 hover:text-red-800'
    : baseDisabledLock
    ? 'text-gray-500'
    : isUnset
    ? 'text-gray-400 opacity-40'
    : 'text-gray-500 hover:text-blue-600'

  return (
    <Tooltip message={tooltipLabel}>
      <button
        type="button"
        aria-label={tooltipLabel}
        aria-pressed={locked}
        disabled={!canLock}
        onClick={handleToggle}
        className={`inline-flex items-center justify-center rounded p-0.5 transition-colors
          ${color}
          ${canLock && !inheritedLock ? 'cursor-pointer' : 'cursor-default'}
          ${className}`}
      >
        <Icon className={iconClass} />
      </button>
    </Tooltip>
  )
}
